import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Tenders from "@/pages/Tenders";
import TenderNew from "@/pages/TenderNew";
import TenderDetail from "@/pages/TenderDetail";
import Competitors from "@/pages/Competitors";
import PricingCalculator from "@/pages/PricingCalculator";
import Admin from "@/pages/Admin";
import Trca from "@/pages/Trca";
import TrcaSchemaGate from "@/components/TrcaSchemaGate";
import TrcaResults from "@/pages/TrcaResults";
import TrcaQualifier from "@/pages/TrcaQualifier";
import PaymentSuccess from "@/pages/PaymentSuccess";
import TrcaSubmissions from "@/pages/admin/TrcaSubmissions";
import TrcaDetail from "@/pages/admin/TrcaDetail";
import TesmSubmissions from "@/pages/admin/TesmSubmissions";
import TesmDetail from "@/pages/admin/TesmDetail";
import BnbdSubmissions from "@/pages/admin/BnbdSubmissions";
import BnbdDetail from "@/pages/admin/BnbdDetail";
import PceiSubmissions from "@/pages/admin/PceiSubmissions";
import PceiDetail from "@/pages/admin/PceiDetail";
import PcabSubmissions from "@/pages/admin/PcabSubmissions";
import PcabDetail from "@/pages/admin/PcabDetail";
import TapiDetail from "@/pages/admin/TapiDetail";
import Companies from "@/pages/admin/Companies";
import CompanyDetail from "@/pages/admin/CompanyDetail";
import TenderReport from "@/pages/TenderReport";
import DocumentHub from "@/components/DocumentHub";
import PlanGate from "@/components/PlanGate";
import Plans from "@/pages/Plans";
import TrcaEpcPage from "@/pages/TrcaEpcPage";
import TrcaEpcSubmissions from "@/pages/admin/TrcaEpcSubmissions";
import TrcaEpcDetail from "@/pages/admin/TrcaEpcDetail";
import FrameworkLocks from "@/pages/admin/FrameworkLocks";
import FrameworkLocksUser from "@/pages/admin/FrameworkLocksUser";
import FrameworkEditorList from "@/pages/admin/FrameworkEditorList";
import FrameworkEditor from "@/pages/admin/FrameworkEditor";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ style: { borderRadius: 2, fontFamily: '"IBM Plex Sans", sans-serif' } }} />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/trca" element={<TrcaSchemaGate><Trca /></TrcaSchemaGate>} />
            <Route path="/trca-qualifier" element={<TrcaQualifier />} />
            <Route path="/trca/results/:id" element={<TrcaSchemaGate><TrcaResults /></TrcaSchemaGate>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />

            {/* Protected app */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<PlanGate feature="tenders" title="Dashboard"><Dashboard /></PlanGate>} />
              <Route path="/tenders" element={<PlanGate feature="tenders" title="Tender List"><Tenders /></PlanGate>} />
              <Route path="/tenders/new" element={<PlanGate feature="tenders" title="New Tender"><TenderNew /></PlanGate>} />
              <Route path="/tenders/:id" element={<PlanGate feature="tenders" title="Tender"><TenderDetail /></PlanGate>} />
              <Route path="/competitors" element={<Competitors />} />
              <Route path="/pricing" element={<PricingCalculator />} />
              <Route path="/plans" element={<Plans />} />
              {/* TRCA EPC Assessment (organisation-level) — separate from legacy /trca */}
              <Route path="/trca-epc" element={<TrcaEpcPage />} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
              <Route path="/admin/trca-epc" element={<ProtectedRoute adminOnly><TrcaEpcSubmissions /></ProtectedRoute>} />
              <Route path="/admin/trca-epc/:id" element={<ProtectedRoute adminOnly><TrcaEpcDetail /></ProtectedRoute>} />
              <Route path="/admin/framework-locks" element={<ProtectedRoute adminOnly><FrameworkLocks /></ProtectedRoute>} />
              <Route path="/admin/framework-locks/:user_id" element={<ProtectedRoute adminOnly><FrameworkLocksUser /></ProtectedRoute>} />
              <Route path="/admin/framework-editor" element={<ProtectedRoute adminOnly><FrameworkEditorList /></ProtectedRoute>} />
              <Route path="/admin/framework-editor/:slug" element={<ProtectedRoute adminOnly><FrameworkEditor /></ProtectedRoute>} />
              <Route path="/admin/trca" element={<ProtectedRoute adminOnly><TrcaSubmissions /></ProtectedRoute>} />
              <Route path="/admin/trca/:id" element={<ProtectedRoute adminOnly><TrcaSchemaGate><TrcaDetail /></TrcaSchemaGate></ProtectedRoute>} />
              <Route path="/admin/tesm" element={<ProtectedRoute adminOnly><TesmSubmissions /></ProtectedRoute>} />
              <Route path="/admin/tesm/:id" element={<ProtectedRoute adminOnly><TesmDetail /></ProtectedRoute>} />
              <Route path="/admin/bnbd" element={<ProtectedRoute adminOnly><BnbdSubmissions /></ProtectedRoute>} />
              <Route path="/admin/bnbd/:id" element={<ProtectedRoute adminOnly><BnbdDetail /></ProtectedRoute>} />
              <Route path="/admin/pcei" element={<ProtectedRoute adminOnly><PceiSubmissions /></ProtectedRoute>} />
              <Route path="/admin/pcei/:id" element={<ProtectedRoute adminOnly><PceiDetail /></ProtectedRoute>} />
              <Route path="/admin/pcab" element={<ProtectedRoute adminOnly><PcabSubmissions /></ProtectedRoute>} />
              <Route path="/admin/pcab/:id" element={<ProtectedRoute adminOnly><PcabDetail /></ProtectedRoute>} />
              <Route path="/admin/tapi/:id" element={<ProtectedRoute adminOnly><TapiDetail /></ProtectedRoute>} />
              <Route path="/admin/companies" element={<ProtectedRoute adminOnly><Companies /></ProtectedRoute>} />
              <Route path="/admin/companies/:id" element={<ProtectedRoute adminOnly><CompanyDetail /></ProtectedRoute>} />

              {/* dashboard_restructure_spec — new sidebar routes */}
              <Route path="/tender-list" element={<Navigate to="/tenders" replace />} />
              <Route path="/tender-report" element={<PlanGate feature="tenders" title="Tender Report"><TenderReport /></PlanGate>} />
              <Route path="/docs/clients" element={<DocumentHub module="client" eyebrow="Modules" title="Clients" subtitle="Client-related materials — past tenders, RFPs, MSAs." />} />
              <Route path="/docs/competitors" element={<DocumentHub module="competitor" eyebrow="Modules" title="Competitors" subtitle="Competitor rate cards, past-bid intelligence, market intel." />} />
              <Route path="/docs/vendors" element={<DocumentHub module="vendor" eyebrow="Modules" title="Vendors" subtitle="Vendor rate cards, MSAs, performance history." />} />
              <Route path="/formats/general" element={<PlanGate feature="format_general" title="General Formats"><DocumentHub module="format_general" eyebrow="Formats" title="General" subtitle="Tender Key Elements · BNBD Approval · Tender Checklist · Tender Result."
                categoryItems={[
                  { value: "Tender Key Elements", label: "Tender Key Elements" },
                  { value: "BNBD Approval", label: "BNBD Approval" },
                  { value: "Tender Checklist", label: "Tender Checklist" },
                  { value: "Tender Result", label: "Tender Result" },
                ]} /></PlanGate>} />
              <Route path="/formats/technical" element={<PlanGate feature="format_technical" title="Technical Formats"><DocumentHub module="format_technical" eyebrow="Formats" title="Technical" subtitle="Manpower / Equipment histogram · Project Schedule · Execution Plan."
                categoryItems={[
                  { value: "Manpower Histogram", label: "Manpower Histogram" },
                  { value: "Equipment Histogram", label: "Equipment Histogram" },
                  { value: "Project Schedule", label: "Project Schedule" },
                  { value: "Execution Plan", label: "Execution Plan" },
                ]} /></PlanGate>} />
              <Route path="/formats/commercial" element={<PlanGate feature="format_commercial" title="Commercial Formats"><DocumentHub module="format_commercial" eyebrow="Formats" title="Commercial" subtitle="Cost Summary Sheet · Cash Flow Graph · Proposal Summary."
                categoryItems={[
                  { value: "Cost Summary Sheet", label: "Cost Summary Sheet" },
                  { value: "Cash Flow Graph", label: "Cash Flow Graph" },
                  { value: "Proposal Summary", label: "Proposal Summary" },
                ]} /></PlanGate>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
